import React from 'react';
import { makeStyles } from '@material-ui/core';
import FilterChip from './FilterChip';
import TeamSelect from '../../shared/TeamSelect';
import { FILTER_COLORS, ActiveFilter } from './types';
import { CompetenceFacetCount } from 'utils/wemble-api.generated';

const useStyles = makeStyles({
  addFilterButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '6px 12px',
    borderRadius: 15,
    border: '3px dotted #E8E8E8',
    backgroundColor: 'transparent',
    color: '#ccc',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    transition: 'border-color 0.15s, color 0.15s, transform 0.15s',
    whiteSpace: 'nowrap',
    '@media (hover: hover)': {
      '&:hover': {
        borderColor: '#ccc',
        color: '#aaa',
      },
    },
    '&:active': {
      transform: 'scale(0.93)',
    },
  },
});

interface SearchFilterBarProps {
  teams: string[];
  teamFacets?: any[];
  onTeamsChange: (teams: string[]) => void;
  advancedFilters: ActiveFilter[];
  competenceFacets?: CompetenceFacetCount[];
  onAdvancedFilterClick: (index: number) => void;
  onAdvancedFilterRemove: (index: number) => void;
  onAddFilter: (anchorEl: HTMLElement) => void;
  onAddFilterHover: (anchorEl: HTMLElement) => void;
  onAddFilterLeave?: () => void;
  teamModalOpen?: boolean;
  onTeamModalClose?: () => void;
  isNonDefault?: boolean;
  onReset?: () => void;
}

const SearchFilterBar = ({
  teams,
  teamFacets,
  onTeamsChange,
  advancedFilters,
  competenceFacets,
  onAdvancedFilterClick,
  onAdvancedFilterRemove,
  onAddFilter,
  onAddFilterHover,
  onAddFilterLeave,
  teamModalOpen,
  onTeamModalClose,
  isNonDefault,
  onReset,
}: SearchFilterBarProps) => {

  const getCompetenceName = (competenceId: string): string => {
    return competenceFacets?.find(c => c._id === competenceId)?.name || 'Competency';
  };

  const classes = useStyles();

  const getAdvancedLabel = (filter: ActiveFilter): string => {
    switch (filter.type) {
      case 'title':
        if (filter.titles.length <= 2) return filter.titles.join(', ');
        return `${filter.titles[0]} +${filter.titles.length - 1}`;
      case 'competence':
        return getCompetenceName(filter.competenceId) + (filter.minLevel != null ? ` (${filter.minLevel}+)` : '');
      case 'experience': {
        const names = filter.labels.length <= 2 ? filter.labels.join(', ') : `${filter.labels[0]} +${filter.labels.length - 1}`;
        return filter.minHours ? `${names} (${filter.minHours}h+)` : names;
      }
      default:
        return 'Filter';
    }
  };

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 8,
      padding: '2px 0',
      userSelect: 'none',
      WebkitUserSelect: 'none',
    } as React.CSSProperties}>
      <TeamSelect
        teams={teamFacets}
        value={teams}
        onChange={onTeamsChange}
        variant="chip"
        externalOpen={teamModalOpen}
        onExternalClose={onTeamModalClose}
      />
      {(() => {
        const competenceFilters = advancedFilters.filter(f => f.type === 'competence');
        const otherFilters = advancedFilters.filter(f => f.type !== 'competence');
        return (
          <>
            {otherFilters.map((filter) => {
              const originalIndex = advancedFilters.indexOf(filter);
              return (
                <FilterChip
                  key={`${filter.type}-${originalIndex}`}
                  label={getAdvancedLabel(filter)}
                  color={(filter.type === 'experience' && FILTER_COLORS[`experience:${filter.dimension}`]) || FILTER_COLORS[filter.type] || FILTER_COLORS.title}
                  onClick={() => onAdvancedFilterClick(originalIndex)}
                  onRemove={() => onAdvancedFilterRemove(originalIndex)}
                />
              );
            })}
            {competenceFilters.length === 1 && (
              <FilterChip
                key="competence-0"
                label={getAdvancedLabel(competenceFilters[0])}
                color={FILTER_COLORS.competence}
                onClick={() => onAdvancedFilterClick(advancedFilters.indexOf(competenceFilters[0]))}
                onRemove={() => onAdvancedFilterRemove(advancedFilters.indexOf(competenceFilters[0]))}
                maxWidth={200}
              />
            )}
            {competenceFilters.length > 1 && (
              <FilterChip
                key="competence-group"
                label={`${competenceFilters.length} competencies`}
                color={FILTER_COLORS.competence}
                onClick={() => onAdvancedFilterClick(advancedFilters.indexOf(competenceFilters[0]))}
                onRemove={() => {
                  const indices = competenceFilters.map(f => advancedFilters.indexOf(f)).sort((a, b) => b - a);
                  indices.forEach(i => onAdvancedFilterRemove(i));
                }}
              />
            )}
          </>
        );
      })()}
      <div
        className={classes.addFilterButton}
        onClick={e => onAddFilter(e.currentTarget)}
        onMouseEnter={e => { if (!window.matchMedia('(hover: none)').matches) { onAddFilterHover(e.currentTarget); } }}
        onMouseLeave={() => { if (!window.matchMedia('(hover: none)').matches) { onAddFilterLeave?.(); } }}
      >
        + Add filter
      </div>
      {isNonDefault && onReset && (
        <div
          onClick={onReset}
          style={{
            marginLeft: 'auto',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 24,
            height: 24,
            borderRadius: '50%',
            color: '#bbb',
            fontSize: 14,
            cursor: 'pointer',
            userSelect: 'none',
            transition: 'color 0.15s, background-color 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.color = '#888'; e.currentTarget.style.backgroundColor = '#f0f0f0'; }}
          onMouseLeave={e => { e.currentTarget.style.color = '#bbb'; e.currentTarget.style.backgroundColor = 'transparent'; }}
          title="Reset filters"
        >
          ✕
        </div>
      )}
    </div>
  );
};

export default SearchFilterBar;
